import { useCallback, useMemo, useState } from 'react'

import { Button, EmptyState, Layout } from '@loadsmart/loadsmart-ui'

import IllustrationBrokenLink from 'core/ui/assets/illustration-broken-link.svg'
import { plural } from 'core/utils/string'

import { type BulkRate } from '../../domain/BulkRate'
import { type DraftQuote, getQuotesToSubmit } from '../../domain/DraftQuote'
import {
  type AccessorialHistoryCharge,
  type AccessorialCharge,
} from '../../domain/QuoteAccessorial'
import { type SortBy } from '../../domain/SortBy'
import { BulkConfirmSubmissionDialog } from './BulkConfirmSubmissionDialog'
import { BulkSpotQuoteRates } from './BulkSpotQuoteRates'
import { BulkSpotQuoteTabHeader } from './BulkSpotQuoteTabHeader'

export type BulkSpotQuotePendingTabProps = {
  id: string
  pendingRates: Array<BulkRate>
  disabled?: boolean
  accessorialsHistory?: Array<AccessorialHistoryCharge>
  onSubmit: (rates: Array<DraftQuote>) => void
}

export function BulkSpotQuotePendingTab({
  id,
  pendingRates,
  disabled = false,
  accessorialsHistory,
  onSubmit,
}: BulkSpotQuotePendingTabProps) {
  const [sortBy, setSortBy] = useState<SortBy>('deadline')
  const [draftQuotes, setDraftQuotes] = useState<Record<string, DraftQuote>>({})
  const [isSubmitDialogOpened, setIsSubmitDialogOpened] = useState(false)
  const ratesToSubmit = useMemo(() => getQuotesToSubmit(draftQuotes), [draftQuotes])

  const pendingRatesCount = pendingRates.length
  const ratesToSubmitCount = ratesToSubmit.length
  const isSubmitDisabled = ratesToSubmitCount === 0 || disabled

  function handleSubmit() {
    setIsSubmitDialogOpened(true)
  }

  function handleCancelSubmission() {
    setIsSubmitDialogOpened(false)
  }

  function handleConfirmSubmission() {
    setIsSubmitDialogOpened(false)
    onSubmit(ratesToSubmit)
  }

  function handleNotesChange(rateId: string, notes: string) {
    setDraftQuotes(currentQuotes => ({
      ...currentQuotes,
      [rateId]: {
        ...currentQuotes[rateId],
        notes,
      },
    }))
  }

  function handleRateChange(rateId: string, flatRate: number) {
    setDraftQuotes(currentQuotes => ({
      ...currentQuotes,
      [rateId]: {
        ...currentQuotes[rateId],
        flatRate,
      },
    }))
  }

  const handleAccessorialsChange = useCallback(
    (rateId: string, accessorials: Array<AccessorialCharge>) => {
      setDraftQuotes(currentQuotes => ({
        ...currentQuotes,
        [rateId]: {
          ...currentQuotes[rateId],
          accessorials,
        },
      }))
    },
    []
  )

  if (pendingRatesCount === 0) {
    return (
      <Layout.Stack align="center">
        <EmptyState
          illustration={IllustrationBrokenLink}
          description="All good! You don't have pending lanes to bid"
          variant="panel"
        />
      </Layout.Stack>
    )
  }

  const header = `${pendingRatesCount} ${plural('lane', pendingRatesCount)} with pending bids`

  return (
    <>
      {isSubmitDialogOpened && (
        <BulkConfirmSubmissionDialog
          ratesToSubmitCount={ratesToSubmitCount}
          onConfirm={handleConfirmSubmission}
          onCancel={handleCancelSubmission}
        />
      )}
      <Layout.Stack>
        <BulkSpotQuoteTabHeader
          header={header}
          sortBy={sortBy}
          onSortChange={value => setSortBy(value)}
          suffix={
            <Button
              type="button"
              variant="primary"
              disabled={isSubmitDisabled}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          }
        />
        <BulkSpotQuoteRates
          sortBy={sortBy}
          rates={pendingRates}
          onRateChange={handleRateChange}
          onNotesChange={handleNotesChange}
          onAccessorialsChange={handleAccessorialsChange}
          disabled={disabled}
          accessorialsHistory={accessorialsHistory}
          draftQuotes={draftQuotes}
        />
      </Layout.Stack>
    </>
  )
}
